import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, Phone } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1625047509248-ec889cbff17f?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const STATS = [
  { value: "6 j/7", label: "Ouvert du lundi au samedi" },
  { value: "100%", label: "Toutes marques" },
  { value: "0€", label: "Devis gratuit" },
  { value: "24h", label: "Réponse à votre demande" },
];

export const Hero = ({ onNavigate }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  const lines = [
    { text: "AUTO", color: "text-[#F8FAFC]" },
    { text: "MECA", color: "text-[#F8FAFC]" },
    { text: "PRO", color: "text-[#0066FF]" },
  ];

  return (
    <section ref={ref} id="accueil" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden noise-overlay">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <img
          src={HERO_IMG}
          alt="Atelier de réparation automobile AUTO MECA PRO à Bellignat"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#07090E]/50 via-[#07090E]/80 to-[#07090E]" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 70% 30%, rgba(0,102,255,0.22) 0%, rgba(7,9,14,0) 60%)" }} />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full pt-36 pb-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="w-2 h-2 rounded-full bg-[#00E5FF] animate-pulse" />
          <span className="font-mono-tech text-xs uppercase tracking-[0.3em] text-[#00E5FF]">
            AMP Services 01 — Bellignat (Ain)
          </span>
        </motion.div>

        <h1 className="font-display font-extrabold uppercase leading-[0.88] tracking-tighter text-[17vw] sm:text-[13vw] lg:text-[9.5rem]">
          {lines.map((l, i) => (
            <span key={l.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${l.color}`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
              >
                {l.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="max-w-md text-base md:text-lg text-slate-300 leading-relaxed"
          >
            Entretien, réparation et diagnostic toutes marques au cœur du Haut-Bugey.
            Votre garage de confiance à Bellignat, à deux minutes d'Oyonnax.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95 }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-booking-button"
              onClick={() => onNavigate("#contact")}
              className="group rounded-full bg-[#0066FF] hover:bg-[#0052CC] text-white font-semibold px-7 py-3.5 flex items-center gap-2 transition-[background-color,box-shadow,transform] duration-300 hover:shadow-[0_0_32px_rgba(0,102,255,0.55)] hover:-translate-y-0.5"
            >
              Prendre rendez-vous
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-services-button"
              onClick={() => onNavigate("#services")}
              className="rounded-full border border-white/20 hover:border-[#0066FF] text-white font-semibold px-7 py-3.5 transition-[border-color,background-color] duration-300 hover:bg-[#0066FF]/10"
            >
              Nos services
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.15 }}
          className="mt-14 grid grid-cols-2 md:grid-cols-4 border-t border-white/10"
        >
          {STATS.map((s) => (
            <div key={s.label} className="py-6 pr-6 border-r border-white/10 last:border-r-0 md:[&:nth-child(2)]:border-r">
              <div className="font-display text-2xl md:text-3xl font-extrabold text-white">{s.value}</div>
              <div className="text-xs md:text-sm text-slate-400 mt-1">{s.label}</div>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3 }}
          className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-slate-400 text-sm"
        >
          <span className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#0066FF]" />
            56 Avenue d'Oyonnax, 01100 Bellignat
          </span>
          <a data-testid="hero-phone-link" href="tel:+33426612733" className="flex items-center gap-2 hover:text-white transition-colors duration-300">
            <Phone className="w-4 h-4 text-[#0066FF]" />
            04 26 61 27 33
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
};
